/**
 * Composant QuickReplies - Boutons de réponse rapide pour le chat de positionnement
 * 
 * Affiche des boutons cliquables pour permettre à l'utilisateur de répondre
 * rapidement aux questions de l'agent de positionnement.
 */

import React from 'react';
import { QuickReply } from '../../types/positioning';
import { 
  User, Building2, Target, MessageSquare, Sparkles, 
  Link, Edit3, SkipForward, Check, RefreshCw,
  Rocket
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AnimatedIconButton } from './AnimatedIconButton';

interface QuickRepliesProps {
  /** Liste des réponses rapides à afficher */
  replies: QuickReply[];
  /** Callback appelé quand une réponse est sélectionnée */
  onSelect: (reply: QuickReply) => void;
  /** Désactiver les boutons (pendant le chargement) */
  disabled?: boolean;
}

// Mapping des icônes par ID de réponse
const ICON_MAP: Record<string, LucideIcon> = {
  personal: User,
  company: Building2,
  start: Rocket,
  has_url: Link,
  no_url: Edit3,
  skip: SkipForward,
  confirm: Check,
  restart: RefreshCw,
  // Objectifs
  brand_awareness: Target,
  thought_leadership: MessageSquare,
  lead_generation: Target,
  community: User,
  recruitment: User,
  other: Edit3,
  // Tons
  professional: Building2,
  friendly: User,
  inspiring: Sparkles,
  provocative: Target,
  educational: MessageSquare,
  humorous: Sparkles,
};

/**
 * Détermine la couleur de bordure/fond selon le variant
 */
function getVariantStyles(variant: QuickReply['variant'], isHovered: boolean) {
  switch (variant) {
    case 'primary':
      return {
        backgroundColor: isHovered ? 'var(--color-primary)' : 'var(--color-primary-light)',
        borderColor: 'var(--color-primary)',
        color: isHovered ? 'var(--color-text-light)' : 'var(--color-primary)',
      };
    case 'secondary':
      return {
        backgroundColor: isHovered ? 'var(--color-surface-hover)' : 'transparent',
        borderColor: 'var(--color-border)',
        color: 'var(--color-text-secondary)',
      };
    case 'outline':
    default:
      return {
        backgroundColor: isHovered ? 'var(--color-surface-hover)' : 'transparent',
        borderColor: 'var(--color-border)',
        color: 'var(--color-text-primary)',
      };
  }
}

// V4-FRONT-POSITIONING-CHIP-DOUBLE-ICON — les labels i18n des quick-replies préfixent déjà un
// emoji (ex "👤 À titre personnel"), or on rend aussi une icône Lucide → double icône. On strip
// l'emoji de tête au rendu (point unique, locale-agnostique) ; le design system garde l'icône Lucide.
const stripLeadingEmoji = (s: string): string =>
  s.replace(/^\s*\p{Extended_Pictographic}(‍\p{Extended_Pictographic}|️)*\s*/u, '');

/**
 * Bouton de réponse rapide individuel
 */
function QuickReplyButton({
  reply, 
  onSelect, 
  disabled 
}: { 
  reply: QuickReply; 
  onSelect: (reply: QuickReply) => void; 
  disabled?: boolean;
}) {
  const [isHovered, setIsHovered] = React.useState(false);
  const IconComponent = ICON_MAP[reply.id] || Sparkles;
  
  if (reply.id === 'start') {
    return (
      <AnimatedIconButton
        icon={IconComponent}
        label={stripLeadingEmoji(reply.text)}
        onClick={() => !disabled && onSelect(reply)}
        disabled={disabled}
      />
    );
  }

  const styles = getVariantStyles(reply.variant, isHovered);

  return (
    <button
      onClick={() => !disabled && onSelect(reply)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border-2"
      style={{
        ...styles,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {IconComponent && <IconComponent className="w-4 h-4" />}
      <span>{stripLeadingEmoji(reply.text)}</span>
    </button>
  );
}

/**
 * Composant principal QuickReplies
 */
export function QuickReplies({ replies, onSelect, disabled = false }: QuickRepliesProps) {
  if (!replies || replies.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {replies.map((reply) => (
        <QuickReplyButton
          key={reply.id}
          reply={reply}
          onSelect={onSelect}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

export default QuickReplies;
